import { Fragment } from "react"
import { MIN_QUERY } from "../lib/fetchAutocomplete"

export type ResultItem = {
  key: string
  title: string
  meta: string
  /** Renders a group heading above the first item that carries a new label. */
  section?: string
}

/** Shared with the combobox input so it can point `aria-activedescendant` here. */
export function resultOptionId(listboxId: string, index: number): string {
  return `${listboxId}-option-${index}`
}

type ResultsListProps = {
  items: ResultItem[]
  loading: boolean
  error: string | null
  query: string
  highlightedIndex: number
  listboxId: string
  onHighlight: (index: number) => void
  onSelect: (index: number) => void
  className?: string
}

export function ResultsList({
  items,
  loading,
  error,
  query,
  highlightedIndex,
  listboxId,
  onHighlight,
  onSelect,
  className,
}: ResultsListProps) {
  const trimmedQuery = query.trim()

  return (
    <div
      id={listboxId}
      className={className ? `bpp-results ${className}` : "bpp-results"}
      role="listbox"
    >
      {loading && <div className="bpp-status">Searching…</div>}
      {error && <div className="bpp-status is-error">{error}</div>}
      {!loading && !error && trimmedQuery.length < MIN_QUERY && items.length === 0 && (
        <div className="bpp-status">Type at least {MIN_QUERY} characters to search</div>
      )}
      {!loading && !error && trimmedQuery.length >= MIN_QUERY && items.length === 0 && (
        <div className="bpp-status">No results found for &ldquo;{query}&rdquo;</div>
      )}
      {!loading &&
        !error &&
        items.map((item, index) => (
          <Fragment key={item.key}>
            {item.section && item.section !== items[index - 1]?.section && (
              <div className="bpp-result-section">{item.section}</div>
            )}
            <button
              type="button"
              role="option"
              id={resultOptionId(listboxId, index)}
              aria-selected={index === highlightedIndex}
              className={`bpp-result${index === highlightedIndex ? " is-highlighted" : ""}`}
              onMouseEnter={() => onHighlight(index)}
              onClick={() => onSelect(index)}
            >
              <div>
                <div className="bpp-result-title">{item.title}</div>
                <div className="bpp-result-meta">{item.meta}</div>
              </div>
            </button>
          </Fragment>
        ))}
    </div>
  )
}
